import { Ref } from "vue";

import * as tmi from "@/constants";

import { useProductsStore } from "@/stores/Products";
import { usePermissionsStore } from "@/stores/Permissions";

import type { ActionConfig, ModelPortfolioTableConfig } from "@/types/products/product-config";


import {
    articleKey,
    articleTutorialsActionConfig,
    bonusReportKey,
    cptLessonsKey,
    cptProductCourseActionConfig,
    dmiLessonsKey,
    dmiProductCourseActionConfig,
    dmtLessonsKey,
    dmtProductCourseActionConfig,
    fiveStarAlertActionConfig,
    fiveStarModelPortfolioActionConfig,
    fiveStarModelPortfolioKey,
    fiveStarTradeAlertKey,
    introductionVideoActionConfig,
    majorIndexBreadthActionConfig,
    majorIndexBreadthKey,
    modelPortfolioActionConfig,
    modelPortfolioKey,
    mttVimeoKey,
    nyseBpiActionConfig,
    nyseBpiKey,
    // stockReplacementStrategyCalculatorKey,
    // stockReplacementStrategyCalculatorActionConfig,
    osLessonsKey,
    osProductCourseActionConfig,
    osVimeoKey,
    portfolioBuilderActionConfig,
    portfolioBuilderKey,
    positionSizeCalculatorActionConfig,
    positionSizeCalculatorKey,
    productAlertActionConfig,
    productArticlesActionConfig,
    productBonusReportsActionConfig,
    psLessonsKey,
    psProductCourseActionConfig,
    relativeStrengthMatrixActionConfig,
    relativeStrengthMatrixKey,
    scannerKey,
    sectorAlertsActionConfig,
    sectorAlertsKey,
    sectorFocusSpotlightActionConfig,
    sectorFocusSpotlightKey,
    sectorsActionConfig,
    sectorsKey,
    sectorSpotlightActionConfig,
    sectorSpotlightKey,
    sectorTreeActionConfig,
    sectorTreeKey,
    sfLessonsKey,
    sfProductCourseActionConfig,
    spProductCourseActionConfig,
    spLessonsKey,
    spotlightVideoActionConfig,
    spotlightVideosKey,
    spreadCalculatorActionConfig,
    sssHistoryActionConfig,
    sssHistoryKey,
    sssHistoryTabKey,
    sssScannerActionConfig,
    sssScannerKey,
    sssScannerTabKey,
    tamIntroductionVideoActionConfig,
    tamLessonsKey,
    tamProductCourseActionConfig,
    tamVimeoKey,
    tradeAlertKey,
    tradeAlertsScannerActionConfig,
    tspLessonsKey,
    tspModelPortfolioActionConfig,
    tspModelPortfolioKey,
    tspProductCourseActionConfig,
    tutorialsKey,
    webinarActionConfig,
    webinarKey,
    welcomeVideoActionConfig
} from "@/types/products/product-definitions";

export function initializeProduct( slug: string, initialized: Ref< boolean >, logoPath: Ref< string | undefined >, darkLogoPath: Ref< string | undefined > ) {
    if ( !initialized.value ) {
        const { productBySlug } = useProductsStore();
        const product = productBySlug( slug );
        if ( product ) {
            logoPath.value = product.logo;
            darkLogoPath.value = product.logo_dark;

            initialized.value = true;
        }
    }
}

export function configureProductActionsMap( key: string, productActionTopicKeys: string[] ): ReadonlyMap< string, ActionConfig > {
    const permissionsStore = usePermissionsStore();
    const actionsMap = new Map< string, ActionConfig >();

    productActionTopicKeys.forEach( ( actionKey: string ) => {
        switch ( actionKey ) {
            case bonusReportKey:
                permissionsStore.hasBonusReportAccessForProduct( key ) ?
                    actionsMap.set( bonusReportKey, productBonusReportsActionConfig ) :
                    console.debug( `( ${ key } ) - no ${ bonusReportKey } access.` );
                actionsMap.set( bonusReportKey, productBonusReportsActionConfig );
                break;
            case modelPortfolioKey:
                permissionsStore.hasModelPortfolioPermissionsForProduct( key ) ?
                    actionsMap.set( modelPortfolioKey, modelPortfolioActionConfig ) :
                    console.debug( `( ${ key } ) - no ${ modelPortfolioKey } access.` );
                break;
            case fiveStarModelPortfolioKey:
                permissionsStore.hasModelPortfolioPermissionsForProduct( key ) ?
                    actionsMap.set( fiveStarModelPortfolioKey, fiveStarModelPortfolioActionConfig ) :
                    console.debug( `( ${ key } ) - no ${ fiveStarModelPortfolioKey } access.` );
                break;
            case tspModelPortfolioKey:
                actionsMap.set( tspModelPortfolioKey, tspModelPortfolioActionConfig );
                break;
            case tradeAlertKey:
                permissionsStore.hasProductTradeAlertsAccess ?
                    actionsMap.set( tradeAlertKey, productAlertActionConfig ) :
                    console.debug( `( ${ key } ) - no ${ tradeAlertKey } access.` );
                break;
            case fiveStarTradeAlertKey:
                permissionsStore.hasProductTradeAlertsAccess ?
                    actionsMap.set( fiveStarTradeAlertKey, fiveStarAlertActionConfig ) :
                    console.debug( `( ${ key } ) - no ${ fiveStarTradeAlertKey } access.` );
                break;
            case articleKey:
                permissionsStore.hasArticleAccessForProduct( key ) ?
                    actionsMap.set( articleKey, productArticlesActionConfig ) :
                    console.debug( `( ${ key } ) - no ${ articleKey } access.` );
                break;
            case nyseBpiKey:
                permissionsStore.hasNyseBpiAccess ?
                    actionsMap.set( nyseBpiKey, nyseBpiActionConfig ) :
                    console.debug( `( ${ key } ) - no ${ nyseBpiKey } access.` );
                break;
            case sectorsKey:
                permissionsStore.hasSectorAccess ?
                    actionsMap.set( sectorsKey, sectorsActionConfig ) :
                    console.debug( `( ${ key } ) - no ${ sectorsKey } access.` );
                break;
            case sectorAlertsKey:
                permissionsStore.hasSectorAlertsAccess ?
                    actionsMap.set( sectorAlertsKey, sectorAlertsActionConfig ) :
                    console.debug( `( ${ key } ) - no ${ sectorAlertsKey } access.` );
                break;
            case portfolioBuilderKey:
                permissionsStore.hasPortfolioBuilderAccess ?
                    actionsMap.set( portfolioBuilderKey, portfolioBuilderActionConfig ) :
                    console.debug( `( ${ key } ) - no ${ portfolioBuilderKey } access.` );
                break;
            case sectorTreeKey:
                actionsMap.set( sectorTreeKey, sectorTreeActionConfig );
                break;
            case relativeStrengthMatrixKey:
                permissionsStore.hasRelativeStrengthMatrixAccess ?
                    actionsMap.set( relativeStrengthMatrixKey, relativeStrengthMatrixActionConfig ) :
                    console.debug( `( ${ key } ) - no ${ relativeStrengthMatrixKey } access.` );
                break;
            case sectorSpotlightKey:
                permissionsStore.hasArticleAccessForProduct( key ) ?
                    actionsMap.set( sectorSpotlightKey, sectorSpotlightActionConfig ) :
                    console.debug( `( ${ key } ) - no ${ sectorSpotlightKey } access.` );
                break;
            case sectorFocusSpotlightKey:
                permissionsStore.hasArticleAccessForProduct( key ) ?
                    actionsMap.set( sectorSpotlightKey, sectorFocusSpotlightActionConfig ) :
                    console.debug( `( ${ key } ) - no ${ sectorSpotlightKey } access.` );
                break;
            case positionSizeCalculatorKey:
                permissionsStore.hasPositionSizeCalculatorAccess ?
                    actionsMap.set( positionSizeCalculatorKey, positionSizeCalculatorActionConfig ) :
                    console.debug( `( ${ key } ) - no ${ positionSizeCalculatorKey } access.` );
                break;
            case webinarKey:
                permissionsStore.hasWebinarAccessForProduct( key ) ?
                    actionsMap.set( webinarKey, webinarActionConfig ) :
                    console.debug( `( ${ key } ) - no ${ webinarKey } access.` );
                break;
            case majorIndexBreadthKey:
                permissionsStore.hasMajorIndexBreadthAccess ?
                    actionsMap.set( majorIndexBreadthKey, majorIndexBreadthActionConfig ) :
                    console.debug( `( ${ key } ) - no ${ majorIndexBreadthKey } access.` );
                break;
            case cptLessonsKey:
                permissionsStore.hasCourseAccessForProduct( key ) ?
                    actionsMap.set( cptLessonsKey, cptProductCourseActionConfig ) :
                    console.debug( `( ${ key } ) - no ${ cptLessonsKey } access.` );
                break;
            case dmiLessonsKey:
                permissionsStore.hasCourseAccessForProduct( key ) ?
                    actionsMap.set( dmiLessonsKey, dmiProductCourseActionConfig ) :
                    console.debug( `( ${ key } ) - no ${ dmiLessonsKey } access.` );
                break;
            case dmtLessonsKey:
                permissionsStore.hasCourseAccessForProduct( key ) ?
                    actionsMap.set( dmtLessonsKey, dmtProductCourseActionConfig ) :
                    console.debug( `( ${ key } ) - no ${ dmtLessonsKey } access.` );
                break;
            case osLessonsKey:
                permissionsStore.hasCourseAccessForProduct( key ) ?
                    actionsMap.set( osLessonsKey, osProductCourseActionConfig ) :
                    console.debug( `( ${ key } ) - no ${ osLessonsKey } access.` );
                break;
            case psLessonsKey:
                permissionsStore.hasCourseAccessForProduct( key ) ?
                    actionsMap.set( psLessonsKey, psProductCourseActionConfig ) :
                    console.debug( `( ${ key } ) - no ${ psLessonsKey } access.` );
                break;
            case sfLessonsKey:
                permissionsStore.hasCourseAccessForProduct( key ) ?
                    actionsMap.set( sfLessonsKey, sfProductCourseActionConfig ) :
                    console.debug( `( ${ key } ) - no ${ sfLessonsKey } access.` );
                break;
            case spLessonsKey:
                permissionsStore.hasCourseAccessForProduct( key ) ?
                    actionsMap.set( spLessonsKey, spProductCourseActionConfig ) :
                    console.debug( `( ${ key } ) - no ${ spLessonsKey } access.` );
                break;
            case tamLessonsKey:
                permissionsStore.hasCourseAccessForProduct( key ) ?
                    actionsMap.set( tamLessonsKey, tamProductCourseActionConfig ) :
                    console.debug( `( ${ key } ) - no ${ tamLessonsKey } access.` );
                break;
            case tspLessonsKey:
                permissionsStore.hasCourseAccessForProduct( key ) ?
                    actionsMap.set( tspLessonsKey, tspProductCourseActionConfig ) :
                    console.debug( `( ${ key } ) - no ${ tspLessonsKey } access.` );
                break;
            case tutorialsKey:
                permissionsStore.hasArticleAccessForProduct( key ) ?
                    actionsMap.set( tutorialsKey, articleTutorialsActionConfig ) :
                    console.debug( `( ${ key } ) - no ${ tutorialsKey } access.` );
                break;
            case scannerKey:
                permissionsStore.hasArticleAccessForProduct( key ) ?
                    actionsMap.set( scannerKey, tradeAlertsScannerActionConfig ) :
                    console.debug( `( ${ key } ) - no ${ scannerKey } access.` );
                break;
            case spotlightVideosKey:
                permissionsStore.hasArticleAccessForProduct( key ) ?
                    actionsMap.set( spotlightVideosKey, spotlightVideoActionConfig ) :
                    console.debug( `( ${ key } ) - no ${ spotlightVideosKey } access.` );
                break;
            case tmi.SpreadCalculatorKey:
                permissionsStore.hasSpreadCalculatorAccess ?
                    actionsMap.set( tmi.SpreadCalculatorKey, spreadCalculatorActionConfig ) :
                    console.debug( `( ${ key } ) - no ${ tmi.SpreadCalculatorKey } access.` );
                break;
            case mttVimeoKey:
                actionsMap.set( mttVimeoKey, welcomeVideoActionConfig );
                break;
            case tamVimeoKey:
                actionsMap.set( tamVimeoKey, tamIntroductionVideoActionConfig );
                break;
            case osVimeoKey:
                actionsMap.set( osVimeoKey, introductionVideoActionConfig );
                break;
            case sssScannerKey:
                // Map keys are the short URL tab tokens (?tab=scanner|history) —
                // per-product, so no collision with DMI's map; the componentKey
                // inside the config still uses the prefixed global names. Both
                // tabs are unconditional: the SSS exclusive role already gates
                // the whole surface, and History runs on the stub until tmi-3at.
                actionsMap.set( sssScannerTabKey, sssScannerActionConfig );
                break;
            case sssHistoryKey:
                actionsMap.set( sssHistoryTabKey, sssHistoryActionConfig );
                break;
            // case stockReplacementStrategyCalculatorKey:
            //     permissionsStore.hasCourseAccessForProduct( key ) ?
            //         actionsMap.set( stockReplacementStrategyCalculatorKey, stockReplacementStrategyCalculatorActionConfig ) :
            //         console.debug( `( ${ key } ) - no ${ stockReplacementStrategyCalculatorKey } access.` );
            //     break;
        }
    } );

    return actionsMap;
}

export function configureProductPortfolioActionsMap( productName: string, initiatedKey: string, closedKey: string,
                                                     openPositions: ModelPortfolioTableConfig,
                                                     closedPositions: ModelPortfolioTableConfig | null ): ReadonlyMap< string, ActionConfig > {
    const portfolioActionsMap = new Map< string, ActionConfig >();

    portfolioActionsMap.set( openPositions.key, {
        displayText: openPositions.title,
        sortKey: initiatedKey,
        emptyMessage: `There are currently no open positions in ${ productName }`
    } );

    if ( null !== closedPositions ) {
        portfolioActionsMap.set( closedPositions.key, {
            displayText: closedPositions.title,
            sortKey: closedKey,
            emptyMessage: `There are currently no closed positions in ${ productName }`
        } );
    }

    return portfolioActionsMap;
}
